"use client";

// shadcn select https://ui.shadcn.com/docs/components/select
// Retokenized to match Wayflyer redesign (paper.design node 50M-0).
// Base UI Select: `alignItemWithTrigger={false}` preserves Radix popper positioning (WEB-297).

import { Select as SelectPrimitive } from "@base-ui/react/select";
import { Check, NavArrowDown, NavArrowUp } from "iconoir-react";
import * as React from "react";

import { cn } from "@/lib/utils";

type SelectRootProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Root<string>
>;

type SelectProps = Omit<SelectRootProps, "onValueChange"> & {
  onValueChange?: (value: string) => void;
};

function Select({ onValueChange, ...props }: SelectProps) {
  return (
    <SelectPrimitive.Root
      {...props}
      onValueChange={
        onValueChange
          ? (value) => {
              if (value != null) {
                onValueChange(value);
              }
            }
          : undefined
      }
    />
  );
}

const SelectGroup = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Group>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Group
    ref={ref}
    className={cn("scroll-my-1", className)}
    {...props}
  />
));
SelectGroup.displayName = "SelectGroup";

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Value
    ref={ref}
    className={cn("line-clamp-1 flex flex-1 text-left", className)}
    {...props}
  />
));
SelectValue.displayName = "SelectValue";

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "text-ds-body-md text-grey-900 data-placeholder:text-grey-500 bg-grey-0 rounded-ds-m flex h-11 w-full items-center justify-between border px-3 py-1 whitespace-nowrap",
      "border-grey-200 hover:border-grey-500",
      "transition-colors duration-[180ms] [transition-timing-function:cubic-bezier(0.2,0.8,0.2,1)]",
      "focus-visible:border-[1.5px] focus-visible:border-transparent focus-visible:outline-none",
      "focus-visible:[background-image:linear-gradient(var(--grey-0),var(--grey-0)),var(--gradient-focus)] focus-visible:[background-clip:padding-box,border-box] focus-visible:[background-origin:border-box]",
      "focus-visible:[box-shadow:var(--shadow-focus-glow)]",
      "data-popup-open:border-[1.5px] data-popup-open:border-transparent",
      "data-popup-open:[background-image:linear-gradient(var(--grey-0),var(--grey-0)),var(--gradient-focus)] data-popup-open:[background-clip:padding-box,border-box] data-popup-open:[background-origin:border-box]",
      "data-popup-open:[box-shadow:var(--shadow-focus-glow)]",
      "aria-[invalid=true]:border-red-500 aria-[invalid=true]:bg-red-50",
      "disabled:bg-grey-50 disabled:border-grey-100 disabled:text-grey-300 disabled:cursor-not-allowed",
      "[&>svg]:transition-transform [&>svg]:duration-[180ms] [&[data-popup-open]>svg]:rotate-180",
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon
      render={
        <NavArrowDown className="text-grey-600 size-4 shrink-0" aria-hidden />
      }
    />
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = "SelectTrigger";

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpArrow>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpArrow>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpArrow
    ref={ref}
    className={cn(
      "text-grey-600 flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <NavArrowUp className="size-4" aria-hidden />
  </SelectPrimitive.ScrollUpArrow>
));
SelectScrollUpButton.displayName = "SelectScrollUpButton";

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownArrow>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownArrow>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownArrow
    ref={ref}
    className={cn(
      "text-grey-600 flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <NavArrowDown className="size-4" aria-hidden />
  </SelectPrimitive.ScrollDownArrow>
));
SelectScrollDownButton.displayName = "SelectScrollDownButton";

type SelectContentProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Popup
> &
  Pick<
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Positioner>,
    "align" | "alignOffset" | "side" | "sideOffset" | "alignItemWithTrigger"
  > & {
    /** @deprecated Radix compat — popper positioning is always used. */
    position?: "popper" | "item-aligned";
  };

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Popup>,
  SelectContentProps
>(
  (
    {
      className,
      children,
      position = "popper",
      side = "bottom",
      sideOffset = 4,
      align = "center",
      alignOffset = 0,
      alignItemWithTrigger = false,
      ...props
    },
    ref
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className="isolate z-50"
      >
        <SelectPrimitive.Popup
          ref={ref}
          className={cn(
            "bg-grey-0 text-grey-900 border-grey-100 shadow-ds-sm rounded-ds-m relative z-50 min-w-[8rem] border",
            "origin-(--transform-origin)",
            "max-h-(--available-height) overflow-x-hidden overflow-y-auto",
            "data-open:animate-in data-closed:animate-out",
            "data-open:fade-in-0 data-closed:fade-out-0",
            "data-closed:duration-[180ms] data-open:duration-[220ms]",
            "data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1",
            position === "popper" &&
              "w-(--anchor-width) data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
            className
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List className="p-1">
            {children}
          </SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
);
SelectContent.displayName = "SelectContent";

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.GroupLabel>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.GroupLabel>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.GroupLabel
    ref={ref}
    className={cn("text-ds-label-md text-grey-600 px-2 py-1.5", className)}
    {...props}
  />
));
SelectLabel.displayName = "SelectLabel";

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "text-ds-body-md text-grey-900 rounded-ds-s relative flex w-full cursor-default items-center py-1.5 pr-8 pl-2 outline-none select-none",
      "data-highlighted:bg-grey-50 data-selected:text-pulse-700",
      "data-disabled:pointer-events-none data-disabled:opacity-50",
      className
    )}
    {...props}
  >
    <span className="absolute right-2 flex size-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="text-pulse-700 size-4" aria-hidden />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = "SelectItem";

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("bg-grey-100 -mx-1 my-1 h-px", className)}
    {...props}
  />
));
SelectSeparator.displayName = "SelectSeparator";

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
